import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTeam } from '@/shared/context/TeamContext'
import { webinarsApi } from '../api/webinars.api'
import type { ReporteWebinarCreateInput, ReporteWebinarUpdateInput } from '../types'

function toSlug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

export function useWebinarsNav() {
  const { selectedArea } = useTeam()
  const { data, isLoading } = useQuery({
    queryKey: ['reportes-webinars', selectedArea?.id, { page: 1, page_size: 500 }],
    queryFn: () => webinarsApi.getReportes({ page_size: 500 }),
  })

  const grupos = useMemo(() => {
    if (!data?.results) return []
    const seen = new Map<number, string>()
    for (const r of data.results) {
      if (!seen.has(r.grupo_id)) seen.set(r.grupo_id, r.grupo_nombre)
    }
    return Array.from(seen, ([id, name]) => ({ id, name, slug: toSlug(name) }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [data])

  return { grupos, isLoading }
}

export function useReportesWebinars(params?: {
  page?: number
  page_size?: number
  search?: string
  grupo_id?: number
  activo?: boolean
}) {
  const { selectedArea } = useTeam()
  return useQuery({
    queryKey: ['reportes-webinars', selectedArea?.id, params],
    queryFn: () => webinarsApi.getReportes(params),
  })
}

export function useCreateReporteWebinar() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: ReporteWebinarCreateInput) =>
      webinarsApi.createReporte(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportes-webinars'] })
    },
  })
}

export function useUpdateReporteWebinar() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: ReporteWebinarUpdateInput }) =>
      webinarsApi.updateReporte(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportes-webinars'] })
    },
  })
}

export function useDeleteReporteWebinar() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => webinarsApi.deleteReporte(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportes-webinars'] })
    },
  })
}

export function useToggleActivoWebinar() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => webinarsApi.toggleActivo(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportes-webinars'] })
    },
  })
}

export function useGruposDisponiblesWebinars() {
  return useQuery({
    queryKey: ['grupos-disponibles-webinars'],
    queryFn: () => webinarsApi.getGruposDisponibles(),
  })
}
